import { useId } from 'react';
import type { Sport } from '@lobi/shared';

// nogometna lopta: točke peterokuta oko središta (kut 0 = gore)
const pent = (cx: number, cy: number, r: number, rot: number) =>
  Array.from({ length: 5 }, (_, i) => {
    const a = ((rot + i * 72 - 90) * Math.PI) / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as const;
  });
const pathOf = (pts: readonly (readonly [number, number])[]) => `M${pts.map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`).join('L')}Z`;
const BALL = (() => {
  const inner = pent(12, 12, 3.6, 0);
  const patches = inner.map((_, i) => {
    const a = ((i * 72 - 90) * Math.PI) / 180;
    return pathOf(pent(12 + 10.4 * Math.cos(a), 12 + 10.4 * Math.sin(a), 3.4, i * 72 + 180));
  });
  // šav od vrha peterokuta do zakrpe na rubu
  const seams = inner
    .map(([x, y]) => {
      const dx = x - 12,
        dy = y - 12,
        k = 6.2 / Math.hypot(dx, dy);
      return `M${x.toFixed(2)} ${y.toFixed(2)}L${(12 + dx * k).toFixed(2)} ${(12 + dy * k).toFixed(2)}`;
    })
    .join('');
  return { center: pathOf(inner), patches, seams };
})();

/** Jednobojne ikone sportova (SPORT_ICON iz stare aplikacije), u boji teksta. */
export function SportIcon({ sport, size = 20 }: { sport: Sport; size?: number }) {
  const mask = useId(); // padel: rupice reketa kroz masku, jedinstven id po ikoni
  const common = { width: size, height: size, viewBox: '0 0 24 24', 'aria-hidden': true } as const;
  if (sport === 'tenis')
    return (
      <svg {...common} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="12" cy="12" r="8.5" />
        <path d="M5.2 7.2c3.2 2.6 3.2 7 0 9.6M18.8 7.2c-3.2 2.6-3.2 7 0 9.6" />
      </svg>
    );
  if (sport === 'nogomet')
    // klasična lopta (vlasnik 19. 10. 2026.: prije je ličila na kotač): peterokut u sredini, pet zakrpa uz rub, kratki šavovi
    return (
      <svg {...common} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round">
        <defs>
          <clipPath id={mask}>
            <circle cx="12" cy="12" r="9" />
          </clipPath>
        </defs>
        <circle cx="12" cy="12" r="9" />
        <path d={BALL.center} fill="currentColor" strokeWidth="1" />
        <g clipPath={`url(#${mask})`} fill="currentColor" stroke="none">
          {BALL.patches.map((d, i) => (
            <path key={i} d={d} />
          ))}
        </g>
        <path d={BALL.seams} strokeWidth="1.2" />
      </svg>
    );
  if (sport === 'kosarka')
    return (
      <svg {...common} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3v18M5.6 5.6c2.6 2.4 3.6 4.4 3.6 6.4s-1 4-3.6 6.4M18.4 5.6c-2.6 2.4-3.6 4.4-3.6 6.4s1 4 3.6 6.4" />
      </svg>
    );
  return (
    <svg {...common} fill="none">
      <defs>
        <mask id={mask}>
          <rect width="24" height="24" fill="#fff" />
          <g fill="#000">
            <circle cx="7.2" cy="7.4" r="1.05" />
            <circle cx="10.4" cy="6.4" r="1.05" />
            <circle cx="6.4" cy="10.6" r="1.05" />
            <circle cx="9.6" cy="9.6" r="1.05" />
            <circle cx="12.6" cy="8.8" r="1.05" />
            <circle cx="8.8" cy="12.8" r="1.05" />
          </g>
        </mask>
      </defs>
      <path d="M13.6 13.8l5.6 5.6" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" />
      <ellipse cx="9.6" cy="9.4" rx="6.6" ry="7.4" transform="rotate(-45 9.6 9.4)" fill="currentColor" mask={`url(#${mask})`} />
    </svg>
  );
}
